import type { WidgetEventName, WidgetEventPayload } from './widget-event-types.js';
import type { WidgetAnalyticsEnvelope } from './build-event-envelope.js';

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/** Legacy GTM event from the previous Teo widget — fired on chat open. */
export const GTM_LEAD_FROM_CHAT = 'lead_from_chat';

export function canPushGtmEvent(): boolean {
  return typeof window !== 'undefined' && Array.isArray(window.dataLayer);
}

/**
 * Push to host GTM `dataLayer` when present.
 * Always sends a namespaced `teo_*` event; mirrors `lead_from_chat` for chat opens.
 */
export function pushGtmWidgetEvent<T extends WidgetEventName>(
  envelope: WidgetAnalyticsEnvelope<T>,
): void {
  if (!canPushGtmEvent()) return;

  const base = {
    teo_event: envelope.event,
    teo_page_session_id: envelope.page_session_id,
    teo_visitor_id: envelope.visitor_id,
    teo_slug: envelope.slug,
    teo_thread_id: envelope.thread_id,
    teo_page_url: envelope.page_url,
    teo_pathname: envelope.pathname,
    teo_page_type: envelope.page_type,
    teo_locale: envelope.locale,
    ...flattenPayload(envelope.payload),
  };

  window.dataLayer!.push({
    event: `teo_${envelope.event}`,
    ...base,
  });

  const legacyEvent = mapLegacyGtmEvent(envelope.event, envelope.payload);
  if (legacyEvent) {
    window.dataLayer!.push({ event: legacyEvent });
  }
}

function mapLegacyGtmEvent(
  event: WidgetEventName,
  _payload: WidgetEventPayload,
): string | null {
  if (event === 'chat_opened') {
    return GTM_LEAD_FROM_CHAT;
  }
  return null;
}

function flattenPayload(
  payload: WidgetEventPayload,
): Record<string, string | number | boolean | null> {
  const flat: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (
      value === null ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      flat[`teo_${key}`] = value;
    }
  }
  return flat;
}
